/**
 * @typedef {{
 *   is_application_confirmable: boolean,
 * }} Order
 *
 * @typedef {{
 *   registration_level: string,
 *   registration_form_id: int,
 *   confirmation_form_id: int,
 *   quotable: boolean
 * }} Course
 */

export function hasQuote({ course, session }) {
    return course.quotable;
}

export function hasAccountType({ }) {
    return true;
}

export function hasAccountTypeStep({ order }) {
    if(order?.is_application_confirmable) {
        return false;
    }
    return hasAccountType({ });
}

/**
 * Account
 */
export function hasAccount({ }) {
    return true;
}

export function hasAccountStep({ order }) {
    if(order?.is_application_confirmable) {
        return false;
    }
    return hasAccount({ });
}

/**
 * Registrants
 */
export function hasRegistrants({ order }) {
    return order?.third_party;
}

export function hasRegistrantsStep({ order }) {
    if(order?.is_application_confirmable) {
        return false;
    }
    return hasRegistrants({ order });
}

/**
 * Options
 */
export function hasOptions({ session, order }) {
    if(order?.is_quote) {
        return false
    }
    return session.options?.length > 0;
}

export function hasOptionsStep({ session, order }) {
    if(order?.is_application_confirmable) {
        return false;
    }
    return hasOptions({ session, order });
}

/**
 * Form
 */
export function hasForm({ course, order }) {
    if(order?.is_quote) {
        return false;
    }
    return !!course.registration_form_id;
}

export function hasFormStep({ course, order }) {
    if(order?.is_application_confirmable) {
        return false;
    }
    return hasForm({ course, order });
}

/**
 * Application confirmation form
 */
export function hasConfirmationForm({ course, order }) {
    if(order?.is_quote) {
        return false;
    }
    if(!order?.is_application_confirmable) {
        return false;
    }
    return !!course.confirmation_form_id;
}

export function hasConfirmationFormStep({ course, order }) {
    return hasConfirmationForm({ course, order });
}

/**
 * Prices
 */
export function hasPrices({ course, order }) {
    if(course.registration_level === 'APPLICATION') {
        if(!order?.is_application_confirmable && order?.is_quote === false) {
            return false;
        }
    }
    return course.prices?.length > 0;
}

export function hasPricesStep({ course, order }) {
    return hasPrices({ course, order });
}

/**
 * Meals
 */
export function hasMeals({ course, session, order }) {
    if(course.registration_level === 'APPLICATION') {
        if(!order?.is_application_confirmable && order?.is_quote === false) {
            return false;
        }
    }
    return session.meals?.length > 0;
}

export function hasMealsStep({ course, session, order }) {
    return hasMeals({ course, session, order });
}

/**
 * Checkout
 */
export function hasCheckout({ course, session, order }) {
    if(order?.is_quote) {
        return false;
    }
    if(hasPrices({ course, order })) {
        return true;
    }
    if(hasMeals({ course, session, order })) {
        // all meals are free
        if(session.meals?.every(meal => meal.prices?.every(price => price.amount === 0))) {
            return false;
        }
        // all chosen meals are free
        if(order?.registrants?.every(registrant =>
            registrant.meals?.length > 0 && registrant.meals?.every(meal => meal.price_amount === 0)
        )) {
            return false;
        }
        return true;
    }
}

export function hasCheckoutStep({ course, session, order }) {
    return hasCheckout({ course, session, order });
}

/**
 * Steps definition + utilities
 */
export function registerSteps({ course, session, order }) {
    const steps = [
        {
            value: 'account-type',
            label: course?.registration_level === 'APPLICATION'
                ? 'Candidature'
                : 'Inscription',
            route: {
                name: order?.id ? 'register-account-type' : 'session-register',
            },
            enabled: hasAccountTypeStep({ order }),
        },
        {
            value: 'account',
            label: 'Coordonnées',
            route: {
                name: 'register-account',
                // name: order?.id ? 'register-account' : 'session-register',
            },
            enabled: hasAccountStep({ order }),
        },
        {
            value: 'registrants',
            label: 'Inscrits',
            route: {
                name: 'register-registrants',
            },
            enabled: hasRegistrantsStep({ order }),
        },
        {
            value: 'options',
            label: 'Options',
            route: {
                name: 'register-options',
            },
            enabled: hasOptionsStep({ session, order }),
        },
        {
            value: 'form',
            label: 'Formulaire',
            route: {
                name: 'register-form',
            },
            enabled: hasFormStep({ course, order }),
        },
        {
            value: 'confirmation-form',
            label: 'Formulaire',
            route: {
                name: 'confirmation-form',
            },
            enabled: hasConfirmationFormStep({ course, order }),
        },
        {
            value: 'prices',
            label: 'Tarifs',
            route: {
                name: 'register-prices',
            },
            enabled: hasPricesStep({ course, order })
        },
        {
            value: 'meals',
            label: 'Options repas',
            route: {
                name: 'register-meals',
            },
            enabled: hasMealsStep({ course, session, order })
        },
        {
            value: 'checkout',
            label: 'Mode de paiement',
            route: {
                name: 'register-checkout',
            },
            enabled: hasCheckoutStep({ course, session, order }),
        },
        {
            value: 'summary',
            label: 'Récapitulatif',
            route: {
                name: 'register-summary',
            },
            enabled: true,
        },
    ]
    .map(step => ({
        ...step,
        route: {
            ...step.route,
            params: {
                orderId: order?.id,
            }
        }
    }));

    return steps
        .filter(step => step.enabled)
        .map(({ enabled, ...step }) => step);
}

export function stepRouteIndex(steps, currentRoute) {
    return steps.findIndex(step => step.route.name === currentRoute.name);
}

export function stepIndex(steps, value) {
    return steps.findIndex(step => step.value === value);
}

export function previousStep(steps, currentRoute) {
    const currentRouteIndex = stepRouteIndex(steps, currentRoute);
    return steps[currentRouteIndex - 1];
}

export function currentStep(steps, currentRoute) {
    const currentRouteIndex = stepRouteIndex(steps, currentRoute);
    return steps[currentRouteIndex];
}

export function nextStep(steps, currentRoute, orderStep) {
    const currentRouteIndex = stepRouteIndex(steps, currentRoute);
    const orderStepIndex = orderStep ? stepIndex(steps, orderStep) : -1;
    if(currentRouteIndex < orderStepIndex) {
        return steps[orderStepIndex];
    }
    return steps[currentRouteIndex + 1];
}


/**
 * Payment modes
 */
export function paymentModes() {
    return [
        { value:'online', label:'Paiement par carte bancaire' },
        { value:'bank_transfer', label:'Paiement par virement' },
        { value:'bank_check', label:'Paiement par chèque' },
        { value:'obligation', label:'Paiement en différé par virement ou mandat administratif' },
        { value:'third_party_funding', label:'Prise en charge par un organisme tiers' },
    ];
}

export function paymentModeLabel(mode) {
    return paymentModes().find(m => m.value === mode)?.label;
}


/**
 * Registrants utils
 */
export function sortRegistrants(registrants) {
    return [...(registrants ?? [])].sort((a,b) => a.self ? -1 : b.self ? 1 : 0);
}

export function registrantIndex(registrants, registrant) {
    return registrants.findIndex(r => r.id === registrant.id);
}


/**
 * Route utils
 */
export function sessionRegisterRoute({ course, session, isQuote }) {
    return {
        name: 'session-register',
        params: {
            courseId: course.id,
            sessionId: session.id
        },
        query: {
            devis: isQuote ? 1 : undefined,
        },
    }
}

/**
 * register button
 */
export function registerButtonLabel(course) {
    if(course.registration_level === 'INSCRIPTION') {
        return `Je m’inscris`
    }
    if(course.registration_level === 'APPLICATION') {
        return `Je candidate`
    }
}
